import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { number, object, string } from 'yup';

import isEmpty from 'lodash/isEmpty';

import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid2';
import Slide from '@mui/material/Slide';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import {
  useCreateDynamicFieldOption,
  useDeleteDynamicFieldOption,
  useUpdateDynamicFieldOption,
} from 'src/pages/DynamicFieldsPage/api';
import DataTable from 'src/components/DataTable';
import TextInput from 'src/components/TextInput';
import { PencilAdornment } from 'src/utils/components';
import ConfirmationDialog from 'src/components/ConfirmationDialog';

import messages from './messages';

type DynamicFieldOptionFormProps = {
  dynamicField: UCM.ExtendedDynamicFieldType;
};

export default function DynamicFieldOptionForm({
  dynamicField,
}: DynamicFieldOptionFormProps) {
  const { formatMessage } = useIntl();

  const [validateOnChange, setValidateOnChange] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [selectedOption, setSelectedOption] =
    useState<UCM.DynamicFieldOptionType | null>(null);

  const { mutate: deleteDynamicFieldOption } = useDeleteDynamicFieldOption();
  const { mutate: createDynamicFieldOption, isPending: isCreating } =
    useCreateDynamicFieldOption();
  const { mutate: updateDynamicFieldOption, isPending: isUpdating } =
    useUpdateDynamicFieldOption();

  const OptionFormRef = useRef<HTMLDivElement>(null);
  const addOptionRef = useRef<HTMLDivElement>(null);

  const initialValues = {
    labelEn: '',
    labelDe: '',
    position: 0,
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnChange: validateOnChange,
    validationSchema: object().shape({
      labelEn: string().required(),
      labelDe: string().required(),
      position: number().integer().min(0).required(),
    }),
    onSubmit: () => {},
  });

  const { values, errors } = formik;

  useEffect(() => {
    if (selectedOption && showForm) {
      formik.setValues({
        position: selectedOption.position,
        labelEn: selectedOption.inputValueEn,
        labelDe: selectedOption.inputValueDe,
      });
    }
  }, [selectedOption, showForm]);

  useEffect(() => {
    if (showForm) hideDiv(addOptionRef.current);
    else hideDiv(OptionFormRef.current);
  }, [showForm]);

  const handleOptionAdd = useCallback(() => {
    setShowForm(true);
  }, []);

  const handleEdit = useCallback((option: UCM.DynamicFieldOptionType) => {
    setSelectedOption(option);
    setShowForm(true);
  }, []);

  const handleDelete = useCallback(
    (uuid: string) => {
      deleteDynamicFieldOption({
        dynamicFieldUuid: dynamicField.uuid,
        optionUuid: uuid,
      });
    },
    [dynamicField.uuid],
  );

  const columns: Array<{
    id: keyof UCM.DynamicFieldOptionType | string;
    label: string | React.ReactNode;
  }> = useMemo(() => {
    return [
      {
        id: 'labelEn',
        label: formatMessage(messages.labelEnColumnLabel),
        render: (_: Object, option: UCM.DynamicFieldOptionType) => (
          <Typography variant="inherit">{option.inputValueEn}</Typography>
        ),
      },
      {
        id: 'labelDe',
        label: formatMessage(messages.labelDeColumnLabel),
        render: (_: Object, option: UCM.DynamicFieldOptionType) => (
          <Typography variant="inherit">{option.inputValueDe}</Typography>
        ),
      },
      {
        id: 'position',
        align: 'center',
        label: formatMessage(messages.positionColumnLabel),
        render: (position: number) => (
          <Typography variant="inherit">{position}</Typography>
        ),
      },
      {
        id: 'actions',
        label: (
          <Fade in={!showForm} mountOnEnter unmountOnExit>
            <Grid
              size={12}
              container
              ref={addOptionRef}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button onClick={handleOptionAdd}>
                {formatMessage(messages.addButton)}
              </Button>
            </Grid>
          </Fade>
        ),
        align: 'center',
        width: 0,
        render: (_: Object, option: UCM.DynamicFieldOptionType) => (
          <Grid display="flex" alignItems="center">
            <Tooltip arrow title={formatMessage(messages.editTooltip)}>
              <IconButton onClick={() => handleEdit(option)}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <ConfirmationDialog
              onConfirm={() => handleDelete(option.uuid)}
              message={formatMessage(messages.deleteDynamicFieldConfirmMessage)}
            >
              {(handleOpen) => (
                <Tooltip arrow title={formatMessage(messages.deleteTooltip)}>
                  <IconButton onClick={handleOpen}>
                    <DeleteForeverOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </ConfirmationDialog>
          </Grid>
        ),
      },
    ];
  }, [showForm]);

  const handleClose = useCallback(() => {
    setValidateOnChange(false);
    formik.resetForm();
    setShowForm(false);
    setSelectedOption(null);
  }, []);

  const handleSave = useCallback(async () => {
    setValidateOnChange(true);

    const errors = await formik.validateForm();
    const isValid = isEmpty(errors);

    if (!isValid) return;

    const updatedValues = {
      ...values,
      dynamicFieldUuid: dynamicField.uuid,
    };

    if (!selectedOption) {
      createDynamicFieldOption(updatedValues, { onSuccess: handleClose });
    } else {
      updateDynamicFieldOption(
        { ...updatedValues, optionUuid: selectedOption.uuid },
        { onSuccess: handleClose },
      );
    }
  }, [values, selectedOption]);

  return (
    <Grid container size={12} spacing={2}>
      <Slide in={showForm} mountOnEnter unmountOnExit direction="left">
        <Grid
          p={2}
          m={1}
          container
          size={12}
          component={Paper}
          flexWrap="nowrap"
          ref={OptionFormRef}
          alignItems="center"
        >
          <Grid container size={12} spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextInput
                fullWidth
                name="labelEn"
                value={values.labelEn}
                error={!!errors.labelEn}
                helperText={errors.labelEn}
                EndIcon={PencilAdornment}
                onChange={formik.handleChange}
                label={formatMessage(messages.labelEnColumnLabel)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextInput
                fullWidth
                name="labelDe"
                value={values.labelDe}
                error={!!errors.labelDe}
                helperText={errors.labelDe}
                EndIcon={PencilAdornment}
                onChange={formik.handleChange}
                label={formatMessage(messages.labelDeColumnLabel)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextInput
                fullWidth
                name="position"
                value={values.position}
                error={!!errors.position}
                helperText={errors.position}
                EndIcon={PencilAdornment}
                onChange={formik.handleChange}
                label={formatMessage(messages.positionColumnLabel)}
              />
            </Grid>
          </Grid>

          <Grid
            container
            size="auto"
            justifyContent="center"
            spacing={1}
            ml={1}
          >
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              loading={isCreating || isUpdating}
            >
              {formatMessage(messages.saveButton)}
            </LoadingButton>

            <Button variant="outlined" color="primary" onClick={handleClose}>
              {formatMessage(messages.cancelButton)}
            </Button>
          </Grid>
        </Grid>
      </Slide>

      <Grid size={12} mt={3}>
        <Typography variant="h6">{formatMessage(messages.title)}</Typography>
      </Grid>

      <Grid size={12}>
        <DataTable
          columns={columns}
          enabledFooter={false}
          rows={dynamicField.options}
        />
      </Grid>
    </Grid>
  );
}

function hideDiv(divRef: HTMLDivElement | null) {
  if (divRef) divRef.style.display = 'none';
}
